import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Networks from './pages/Networks';
import Reports from './pages/Reports';
import Players from './pages/Players';
import Settings from './pages/Settings';
import Import from './pages/Import';
import Debug from './pages/Debug';
import SyncAll from './pages/SyncAll';
import ACCompare from './pages/ACCompare';
import RaketechCompare from './pages/RaketechCompare';
import RaketechUnderpaid from './pages/RaketechUnderpaid';
import RaketechUnsynced from './pages/RaketechUnsynced';
import RaketechAccounts from './pages/RaketechAccounts';
import RaketechReport from './pages/RaketechReport';
import RaketechPostRecon from './pages/RaketechPostRecon';
import RaketechFinalRecon from './pages/RaketechFinalRecon';
import RaketechFinalReport from './pages/RaketechFinalReport';
import RaketechFinalReportV2 from './pages/RaketechFinalReportV2';
import RaketechFinalReportV3 from './pages/RaketechFinalReportV3';
import RaketechFinalReportV4 from './pages/RaketechFinalReportV4';
import RaketechReconDiff from './pages/RaketechReconDiff';
import RaketechDocs from './pages/RaketechDocs';
import RaketechMissingAccounts from './pages/RaketechMissingAccounts';
import SharedReport from './pages/SharedReport';
import { useAuthStore } from './stores/authStore';
import './index.css';

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
        />
        <Route path="/shared/report/:slug" element={<SharedReport />} />
        
        {/* Protected Routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/networks"
          element={
            <ProtectedRoute>
              <Layout>
                <Networks />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/players"
          element={
            <ProtectedRoute>
              <Layout>
                <Players />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/import"
          element={
            <ProtectedRoute>
              <Layout>
                <Import />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/debug"
          element={
            <ProtectedRoute>
              <Layout>
                <Debug />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sync-all"
          element={
            <ProtectedRoute>
              <Layout>
                <SyncAll />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ac-compare"
          element={
            <ProtectedRoute>
              <Layout>
                <ACCompare />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechCompare />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/underpaid"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechUnderpaid />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/unsynced"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechUnsynced />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/accounts"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechAccounts />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/report"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechReport />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/final-recon"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechFinalRecon />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/final-report"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechFinalReport />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/post-recon"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechPostRecon />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/recon-diff"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechReconDiff />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/final-report-v2"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechFinalReportV2 />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/final-report-v3"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechFinalReportV3 />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/final-report-v4"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechFinalReportV4 />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/docs"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechDocs />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raketech/missing-accounts"
          element={
            <ProtectedRoute>
              <Layout>
                <RaketechMissingAccounts />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;

